import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHome,
  FaListAlt,
  FaClipboardList,
  FaChartBar,
  FaCogs,
  FaQuestionCircle,
  FaUsers,
} from 'react-icons/fa';
import { LuSendToBack } from 'react-icons/lu';
import { SiFormstack } from 'react-icons/si';
import { IoCreateSharp } from 'react-icons/io5';
import { BsGraphDownArrow } from 'react-icons/bs';
import { SlGraph } from 'react-icons/sl';
import { MdAdminPanelSettings } from 'react-icons/md';
import { RiOrganizationChart } from 'react-icons/ri';
import { FaList } from 'react-icons/fa6';
import { FaBook } from 'react-icons/fa';
import { Upload } from 'lucide-react';

const MenuItem = ({ icon: Icon, children, to, onClick, isOpen }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2 p-2 rounded-lg hover:bg-base-200 text-base-content transition-colors relative group"
      title={isOpen ? '' : children}
    >
      <Icon className="w-5 h-5" />
      {isOpen ? (
        <span>{children}</span>
      ) : (
        <div className="absolute left-14 z-50 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
          {children}
        </div>
      )}
    </Link>
  );
};

const MenuGroup = ({ icon: Icon, title, children, isOpen }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-base-200 text-base-content transition-colors relative group"
        title={isOpen ? '' : title}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" />
          {isOpen && <span>{title}</span>}
        </div>
        {isOpen && (
          <svg
            className={`w-4 h-4 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
        {!isOpen && (
          <div className="absolute left-14 z-50 bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            {title}
          </div>
        )}
      </button>
      <div className={`ml-2 mt-1 space-y-1 ${isExpanded ? 'block' : 'hidden'}`}>
        {children}
      </div>
    </div>
  );
};

export function Sidebar({ onSidebarToggle }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No hay token en localStorage');
        return;
      }

      console.log('Token enviado:', token);

      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/api/user/profile',
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error de respuesta:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        return;
      }

      const userData = await response.json();
      console.log('Datos del usuario:', userData);
      setIsAdmin(userData.rol === 'Admin');
    } catch (error) {
      console.error('Error completo:', error);
    }
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    onSidebarToggle(!isOpen);
  };

  if (window.parent.location.pathname.includes('/view-form/')) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-5 left-3 z-50 p-2 rounded-full bg-base-100 shadow-lg text-base-content hover:bg-base-200"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 512 512">
            <polygon points="400 145.49 366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 512 512">
            <path d="M64,384H448V341.33H64Zm0-106.67H448V234.67H64ZM64,128v42.67H448V128Z" />
          </svg>
        )}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-6 h-screen pt-16 shadow-lg transition-all duration-200 bg-base-100 border-r border-base-300 overflow-y-auto ${
          isOpen ? 'w-64' : 'w-16'
        }`}
      >
        <div className="p-2 space-y-4">
          <MenuItem to="/" icon={FaHome} isOpen={isOpen}>
            Panel Principal
          </MenuItem>

          <div className="border-t border-base-300" />

          <MenuGroup
            icon={MdAdminPanelSettings}
            title="Gestión admin"
            isOpen={isOpen}
          >
            <MenuItem to="/users" icon={FaUsers} isOpen={isOpen}>
              Usuarios
            </MenuItem>
            <MenuItem
              to="/organizations"
              icon={RiOrganizationChart}
              isOpen={isOpen}
            >
              Organizaciones
            </MenuItem>
            {/* <MenuItem to="/Charge" icon={Upload} isOpen={isOpen}>
              Carga Util
            </MenuItem> */}
          </MenuGroup>

          <div className="border-t border-base-300" />

          <MenuGroup icon={FaBook} title="Mis Formularios" isOpen={isOpen}>
            <MenuItem to="/myforms" icon={FaList} isOpen={isOpen}>
              Ver Formularios
            </MenuItem>
            <MenuItem to="/createform" icon={IoCreateSharp} isOpen={isOpen}>
              Formulario General
            </MenuItem>
          </MenuGroup>
          {isAdmin && (
            <>
              {/* <div className="border-t border-base-300" /> */}

              {/* <MenuGroup icon={FaChartBar} title="Estadísticas" isOpen={isOpen}>
                <MenuItem to="#" icon={BsGraphDownArrow} isOpen={isOpen}>
                  Estadística General
                </MenuItem>
                <MenuItem to="#" icon={SlGraph} isOpen={isOpen}>
                  Estadísticas Personalizadas
                </MenuItem>
              </MenuGroup> */}

              <div className="border-t border-base-300" />

             <MenuGroup icon={FaCogs} title="Configuración" isOpen={isOpen}>
                <MenuItem to="/logs" icon={FaCogs} isOpen={isOpen}>
                  Registros de Actividad
                </MenuItem>
                {/* <MenuItem to="#" icon={FaCogs} isOpen={isOpen}>
                  Seguridad
                </MenuItem> */}
              </MenuGroup>

              <div className="border-t border-base-300" />

              <MenuItem
                to="#"
                icon={FaQuestionCircle}
                isOpen={isOpen}
                className="hover:bg-primary hover:text-primary-content"
              >
                Ayuda
              </MenuItem>
            </>
          )}
        </div>
      </div>
    </>
  );
}
